import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, CheckIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { Button } from '@components/ui/Button'
import { Input } from '@components/ui/Input'
import { AuthService } from '@services/auth/authService'

const forgotPasswordSchema = z.object({
  email: z.string().email('Email invalide'),
})

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>

type ActionStatus = 'none' | 'pending' | 'success' | 'error'

interface ForgotPasswordPageProps {
  pageTitle?: string
  title?: string
  buttonText?: string
}

export const ForgotPasswordPage = ({ 
  pageTitle = 'Mot de passe oublié',
  title = 'Entrez votre adresse mail pour réinitialiser votre mot de passe',
  buttonText = 'Réinitialiser le mot de passe'
}: ForgotPasswordPageProps) => {
  const [status, setStatus] = useState<ActionStatus>('none')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordForm) => {
    setStatus('pending')

    try {
      await AuthService.resetPassword(data.email)
      setStatus('success')
    } catch (error) {
      console.error('Password reset error:', error)
      setStatus('error')
    }
  }

  const canSubmit = status === 'none' || status === 'error'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header with back button */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link
                to="/"
                className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Retour
              </Link>
            </div>
            <h1 className="text-lg font-medium text-gray-900 dark:text-white">
              {pageTitle}
            </h1>
            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <Input
              {...register('email')}
              type="email"
              label="Email"
              placeholder="Adresse email"
              error={errors.email?.message}
              leftIcon={<EnvelopeIcon className="h-4 w-4" />}
              disabled={!canSubmit}
              autoComplete="email"
            />

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={!canSubmit}
              loading={status === 'pending'}
            >
              {status === 'success' ? (
                <div className="flex items-center justify-center">
                  <CheckIcon className="h-5 w-5" />
                </div>
              ) : (
                buttonText
              )}
            </Button>
          </form>

          {/* Status Messages */}
          {(status === 'success' || status === 'error') && (
            <div className="mt-6 text-center">
              <p className={`text-sm ${
                status === 'success' 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {status === 'success' 
                  ? "Si votre compte existe, un email de réinitialisation vous a été envoyé."
                  : "Une erreur est survenue. Veuillez réessayer."
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}